import TextFontUpdater from 'client/components/core/Sidebar/components/Settings/components/Font/TextFontUpdater'
import {
  useUpdateEntity,
  useUpdateProp,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import HtmlAttrId from 'client/components/core/Sidebar/components/Settings/options/HtmlAttrId'
import {
  AlignStyle,
  FontSize,
  ShadowStyle,
} from 'client/components/core/Sidebar/components/Settings/styles'
import BorderRadius from 'client/components/core/Sidebar/components/Settings/styles/BorderRadius'
import LetterSpacing from 'client/components/core/Sidebar/components/Settings/styles/LetterSpacing'
import TextLineHeight from 'client/components/core/Sidebar/components/Settings/styles/TextLineHeight'
import MobileIconUi from 'client/components/core/Sidebar/components/Settings/styles/ui/MobileIconUi'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { getByType } from 'client/reducers/pageReducer'
import { blogSelectors, useBlog, usePage } from 'client/store'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { LatestBlogPostsInterface } from 'common/types/entities/LatestBlogPostsInterface'
import React, { useState } from 'react'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { CheckboxControl } from 'src/editor/shared/settings/checkbox-control'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { MarginGroup } from 'src/editor/shared/settings/groups/margin'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import { RangeElement } from 'src/editor/shared/ui-lib/range-element'
import {
  OptionSingleValueType,
  OptionType,
  SelectNew,
} from 'src/editor/shared/ui-lib/select-new/select-new'
import {
  LATEST_BLOG_POSTS_PLACEHOLDER,
  LATEST_CATEGORY_BLOG_POSTS_PLACEHOLDER,
} from '../entities/BlogLatestPosts'

function getLatestCategoryBlogPostsReplacement(categoryId: number) {
  return LATEST_CATEGORY_BLOG_POSTS_PLACEHOLDER.replace(' ', String(categoryId))
}

function BlogLatestPostsSettings({
  entity,
}: {
  entity: LatestBlogPostsInterface
}) {
  const { t } = useTypedTranslation()
  const updateEntity = useUpdateEntity<LatestBlogPostsInterface>()
  const updateProp = useUpdateProp(entity)
  const { isMobile } = useDeviceModeContext()
  const categories = useBlog(blogSelectors.getBlogCategories)
  const [selectCategoriesError, setSelectCategoriesError] = useState('')

  const entities = usePage(page => page.entities)
  const latestBlogPostEntities = getByType(
    { entities },
    EntityTypeEnum.LatestBlogPosts,
  ).filter(el => el.id !== entity.id)

  const categoriesOptions: OptionType[] = [
    {
      label: t('entity_settings.blog_post_listing.all_categories'),
      value: LATEST_BLOG_POSTS_PLACEHOLDER,
    },
  ]
  categories.forEach(category => {
    categoriesOptions.push({
      label: category.name,
      value: getLatestCategoryBlogPostsReplacement(category.id),
    })
  })

  const checkSelectedCategory = (category: string) => {
    latestBlogPostEntities.some(el => el.postsReplacement === category)
      ? setSelectCategoriesError(
          t('entity_settings.blog_latest_posts.category_error'),
        )
      : setSelectCategoriesError('')
  }

  function updateCategories(option: OptionSingleValueType) {
    if (option) {
      checkSelectedCategory(option.value)
      updateProp('postsReplacement')(option.value)
    }
  }

  const getDefaultCategoriesOption = (categoryReplacement?: string) => {
    if (!categoryReplacement) {
      return categoriesOptions[0]
    }

    const selectedCategoryOption = categoriesOptions.find(
      state => state.value === categoryReplacement,
    )

    if (selectedCategoryOption) {
      return selectedCategoryOption
      // biome-ignore lint/style/noUselessElse: <explanation>
    } else return categoriesOptions[0]
  }

  return (
    <>
      <RangeElement
        label="entity_settings.blog_latest_posts.maximum_posts_per_page"
        onChange={updateProp('maxPosts')}
        value={entity.maxPosts}
        min={1}
        max={10}
        allowNegative={false}
      />
      <RangeElement
        label="entity_settings.blog_post_listing.columns_gap"
        onChange={updateProp(isMobile ? 'mobileRowsGap' : 'rowsGap')}
        value={isMobile ? entity.mobileRowsGap : entity.rowsGap}
        allowNegative={false}
      />
      <SelectNew
        labelText="entity_settings.blog_post_listing.category"
        selectedOption={getDefaultCategoriesOption(entity.postsReplacement)}
        onChange={updateCategories}
        options={categoriesOptions}
        isMulti={false}
        error={selectCategoriesError}
      />
      {!isMobile && (
        <CheckboxControl
          label="entity_settings.blog_post_listing.display_horizontally.label"
          update={updateProp('displayHorizontally')}
          value={entity.displayHorizontally}
        />
      )}
      {entity.displayHorizontally && !isMobile && (
        <>
          <RangeElement
            label="entity_settings.blog_post_listing.rows_gap"
            onChange={updateProp('columnsGap')}
            value={entity.columnsGap}
            min={1}
            max={50}
            allowNegative={false}
          />
          <RangeElement
            label="entity_settings.blog_post_listing.maximum_posts_per_row"
            onChange={updateProp('maxPostsPerRow')}
            value={entity.maxPostsPerRow}
            min={1}
            max={5}
            allowNegative={false}
          />
        </>
      )}
      <GroupTitle>entity_settings.image.header</GroupTitle>
      <ShadowStyle
        shadow={entity.imageBoxShadow}
        mobileShadow={entity.mobileImageBoxShadow}
        update={updateProp('imageBoxShadow')}
        mobileUpdate={updateProp('mobileImageBoxShadow')}
        isTitleNeeded={false}
      />
      <BorderRadius
        label="settings_styles.border_radius.label"
        labelIcon={isMobile ? <MobileIconUi /> : null}
        border={isMobile ? entity.mobileImageBorder : entity.imageBorder}
        update={updateProp(isMobile ? 'mobileImageBorder' : 'imageBorder')}
      />
      <MarginGroup
        label="settings_styles.margin.label"
        margin={isMobile ? entity.mobileImageMargin : entity.imageMargin}
        update={updateProp(isMobile ? 'mobileImageMargin' : 'imageMargin')}
      />
      <GroupTitle>entity_settings.blog_post_listing.title</GroupTitle>
      <FontSize
        update={updateProp('titleFontSize')}
        mobileUpdate={updateProp('mobileTitleFontSize')}
        fontSize={entity.titleFontSize}
        mobileFontSize={entity.mobileTitleFontSize}
      />
      <TextLineHeight
        fontSize={entity.titleFontSize}
        mobileFontSize={entity.mobileTitleFontSize}
        update={updateProp('titleLineHeight')}
        mobileUpdate={updateProp('mobileTitleLineHeight')}
        lineHeight={entity.titleLineHeight}
        mobileLineHeight={entity.mobileTitleLineHeight}
      />
      <TextFontUpdater<LatestBlogPostsInterface>
        entity={entity}
        update={updateEntity}
        fontFamilyKey={'titleFontFamily'}
        fontWeightKey={'titleFontWeight'}
        fontStyleKey={'titleFontStyle'}
        mobileFontFamilyKey={'mobileTitleFontFamily'}
        mobileFontWeightKey={'mobileTitleFontWeight'}
        mobileFontStyleKey={'mobileTitleFontStyle'}
        fontFileIdKey={'titleFontFileId'}
        mobileFontFileIdKey={'mobileTitleFontFileId'}
      />
      <LetterSpacing
        change={
          isMobile
            ? updateProp('mobileTitleLetterSpacing')
            : updateProp('titleLetterSpacing')
        }
        letterSpacing={
          isMobile ? entity.mobileTitleLetterSpacing : entity.titleLetterSpacing
        }
      />
      <AlignStyle
        align={isMobile ? entity.mobileTitleAlign : entity.titleAlign}
        update={
          isMobile ? updateProp('mobileTitleAlign') : updateProp('titleAlign')
        }
        isFlex={false}
      />
      <ColorPickerControl
        update={updateProp('titleColor')}
        mobileUpdate={updateProp('mobileTitleColor')}
        color={entity.titleColor}
        mobileColor={entity.mobileTitleColor}
      />
      <MarginGroup
        label="settings_styles.margin.label"
        margin={isMobile ? entity.mobileTitleMargin : entity.titleMargin}
        update={updateProp(isMobile ? 'mobileTitleMargin' : 'titleMargin')}
      />
      <GroupTitle>entity_settings.blog_post_listing.description</GroupTitle>
      <FontSize
        update={updateProp('descriptionFontSize')}
        mobileUpdate={updateProp('mobileDescriptionFontSize')}
        fontSize={entity.descriptionFontSize}
        mobileFontSize={entity.mobileDescriptionFontSize}
      />
      <TextLineHeight
        fontSize={entity.descriptionFontSize}
        mobileFontSize={entity.mobileDescriptionFontSize}
        update={updateProp('descriptionLineHeight')}
        mobileUpdate={updateProp('mobileDescriptionLineHeight')}
        lineHeight={entity.descriptionLineHeight}
        mobileLineHeight={entity.mobileDescriptionLineHeight}
      />
      <TextFontUpdater<LatestBlogPostsInterface>
        entity={entity}
        update={updateEntity}
        fontFamilyKey={'descriptionFontFamily'}
        fontWeightKey={'descriptionFontWeight'}
        fontStyleKey={'descriptionFontStyle'}
        mobileFontFamilyKey={'mobileDescriptionFontFamily'}
        mobileFontWeightKey={'mobileDescriptionFontWeight'}
        mobileFontStyleKey={'mobileDescriptionFontStyle'}
        fontFileIdKey={'descriptionFontFileId'}
        mobileFontFileIdKey={'mobileDescriptionFontFileId'}
      />
      <AlignStyle
        align={
          isMobile ? entity.mobileDescriptionAlign : entity.descriptionAlign
        }
        update={
          isMobile
            ? updateProp('mobileDescriptionAlign')
            : updateProp('descriptionAlign')
        }
        isFlex={false}
      />
      <ColorPickerControl
        update={updateProp('descriptionColor')}
        mobileUpdate={updateProp('mobileDescriptionColor')}
        color={entity.descriptionColor}
        mobileColor={entity.mobileDescriptionColor}
      />
      <MarginGroup
        label="settings_styles.margin.label"
        margin={
          isMobile ? entity.mobileDescriptionMargin : entity.descriptionMargin
        }
        update={updateProp(
          isMobile ? 'mobileDescriptionMargin' : 'descriptionMargin',
        )}
      />
      <GroupTitle>entity_settings.blog_post_listing.categories</GroupTitle>
      <FontSize
        update={updateProp('categoriesFontSize')}
        mobileUpdate={updateProp('mobileCategoriesFontSize')}
        fontSize={entity.categoriesFontSize}
        mobileFontSize={entity.mobileCategoriesFontSize}
      />
      <TextFontUpdater<LatestBlogPostsInterface>
        entity={entity}
        update={updateEntity}
        fontFamilyKey={'categoriesFontFamily'}
        fontWeightKey={'categoriesFontWeight'}
        fontStyleKey={'categoriesFontStyle'}
        mobileFontFamilyKey={'mobileCategoriesFontFamily'}
        mobileFontWeightKey={'mobileCategoriesFontWeight'}
        mobileFontStyleKey={'mobileCategoriesFontStyle'}
        fontFileIdKey={'categoriesFontFileId'}
        mobileFontFileIdKey={'mobileCategoriesFontFileId'}
      />
      <AlignStyle
        align={isMobile ? entity.mobileCategoriesAlign : entity.categoriesAlign}
        update={
          isMobile
            ? updateProp('mobileCategoriesAlign')
            : updateProp('categoriesAlign')
        }
        isFlex={false}
      />
      <ColorPickerControl
        update={updateProp('categoriesColor')}
        mobileUpdate={updateProp('mobileCategoriesColor')}
        color={entity.categoriesColor}
        mobileColor={entity.mobileCategoriesColor}
      />
      <MarginGroup
        label="settings_styles.margin.label"
        margin={
          isMobile ? entity.mobileCategoriesMargin : entity.categoriesMargin
        }
        update={updateProp(
          isMobile ? 'mobileCategoriesMargin' : 'categoriesMargin',
        )}
      />
      <GroupTitle>entity_settings.blog_post_listing.date</GroupTitle>
      <CheckboxControl
        label="entity_settings.blog_post_listing.show_date.label"
        update={updateProp('showDate')}
        value={entity.showDate}
      />
      {entity.showDate && (
        <>
          <FontSize
            update={updateProp('dateFontSize')}
            mobileUpdate={updateProp('mobileDateFontSize')}
            fontSize={entity.dateFontSize}
            mobileFontSize={entity.mobileDateFontSize}
          />
          <TextFontUpdater<LatestBlogPostsInterface>
            entity={entity}
            update={updateEntity}
            fontFamilyKey={'dateFontFamily'}
            fontWeightKey={'dateFontWeight'}
            fontStyleKey={'dateFontStyle'}
            mobileFontFamilyKey={'mobileDateFontFamily'}
            mobileFontWeightKey={'mobileDateFontWeight'}
            mobileFontStyleKey={'mobileDateFontStyle'}
            fontFileIdKey={'dateFontFileId'}
            mobileFontFileIdKey={'mobileDateFontFileId'}
          />
          <AlignStyle
            align={isMobile ? entity.mobileDateAlign : entity.dateAlign}
            update={
              isMobile ? updateProp('mobileDateAlign') : updateProp('dateAlign')
            }
            isFlex={false}
          />
          <ColorPickerControl
            update={updateProp('dateColor')}
            mobileUpdate={updateProp('mobileDateColor')}
            color={entity.dateColor}
            mobileColor={entity.mobileDateColor}
          />
          <MarginGroup
            label="settings_styles.margin.label"
            margin={isMobile ? entity.mobileDateMargin : entity.dateMargin}
            update={updateProp(isMobile ? 'mobileDateMargin' : 'dateMargin')}
          />
        </>
      )}
      <SizeAndPositionGroup
        padding={isMobile ? entity?.mobilePadding : entity.padding}
        updatePadding={updateProp(isMobile ? 'mobilePadding' : 'padding')}
        margin={isMobile ? entity.mobileMargin : entity.margin}
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
      />
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </>
  )
}

export default BlogLatestPostsSettings
